import { sortAsc } from '../utils/sortOptions';
import { formatPhone, formatCnic } from '../utils/employeeFormatters';
import { createPortal } from 'react-dom';
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import ConfirmModal from '../components/ConfirmModal';

import {
  PlusIcon, PencilIcon, XMarkIcon, ClockIcon,
  UserGroupIcon, MagnifyingGlassIcon, CheckCircleIcon, NoSymbolIcon,
} from '@heroicons/react/24/outline';
import { DEPARTMENTS, DEPT_COLORS, ALL_DESIGNATIONS } from '../constants/staffDesignations';

const parseDate = (d) => {
  if (!d) return '';
  if (typeof d === 'string') return d.slice(0, 10);
  const dt = new Date(d);
  const y = dt.getFullYear();
  const mo = String(dt.getMonth()+1).padStart(2,'0');
  const day = String(dt.getDate()).padStart(2,'0');
  return y + '-' + mo + '-' + day;
};


const EMPTY_FORM = {
  name:'', department:'General', designation:'Peon', employeeId:'', email:'', phone:'', cnic:'',
  address:'', joiningDate:'', monthlySalary:'', absentDeduction:'', lateDeduction:'', notes:'',
};

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .sm * { box-sizing:border-box; }
  .sm { font-family:'Sora',sans-serif; color:#1a1d2e; }
  .sm-title { font-size:1.4rem; font-weight:700; color:#0f1123; letter-spacing:-.02em; margin-bottom:3px; }
  .sm-sub   { font-size:.76rem; color:#9094a8; margin-bottom:1.5rem; }
  .sm-toolbar { display:flex; gap:.7rem; flex-wrap:wrap; align-items:center; margin-bottom:1.2rem; }
  .sm-search-wrap { position:relative; flex:1; max-width:300px; }
  .sm-search-wrap svg { position:absolute; left:11px; top:50%; transform:translateY(-50%); width:15px; height:15px; stroke:#9094a8; pointer-events:none; }
  .sm-search { font-family:'Sora',sans-serif; font-size:.8rem; width:100%; padding:8px 12px 8px 32px; border:1.5px solid #e0e4f0; border-radius:10px; background:white; outline:none; }
  .sm-search:focus { border-color:#3b6ef8; }
  .sm-select { font-family:'Sora',sans-serif; font-size:.8rem; padding:8px 12px; border:1.5px solid #e0e4f0; border-radius:10px; background:white; outline:none; }
  .btn-add { display:inline-flex; align-items:center; gap:6px; padding:8px 18px; border:none; border-radius:10px; background:#3b6ef8; color:white; font-family:'Sora',sans-serif; font-size:.82rem; font-weight:600; cursor:pointer; margin-left:auto; }
  .btn-add svg { width:15px; height:15px; stroke-width:2.5; }
  .sm-panel { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; }
  .sm-tbl-wrap { overflow-x:auto; }
  .sm-tbl { width:100%; border-collapse:collapse; }
  .sm-tbl thead { background:#fafbff; border-bottom:1px solid #edf0f9; }
  .sm-tbl th { padding:10px 14px; text-align:center !important; font-size:.65rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.07em; white-space:nowrap; }
  .sm-tbl tbody tr { border-bottom:1px solid #f3f5fb; }
  .sm-tbl tbody tr:last-child { border-bottom:none; }
  .sm-tbl tbody tr:hover { background:#fafbff; }
  .sm-tbl td { padding:10px 14px; font-size:.8rem; vertical-align:middle; text-align:center !important; }
  .sm-avatar { width:34px; height:34px; border-radius:9px; display:flex; align-items:center; justify-content:center; font-size:.72rem; font-weight:700; color:white; flex-shrink:0; }
  .des-pill { font-size:.66rem; font-weight:700; padding:3px 9px; border-radius:99px; display:inline-flex; white-space:nowrap; }
  .action-btn { width:28px; height:28px; border-radius:7px; border:1px solid #e4e7f0; background:white; cursor:pointer; display:flex; align-items:center; justify-content:center; transition:all .15s; }
  .action-btn svg { width:13px; height:13px; stroke-width:2; }
  .action-btn.edit { color:#3b6ef8; } .action-btn.edit:hover { background:#eef2ff; border-color:#3b6ef8; }
  .action-btn.del  { color:#ef4444; } .action-btn.del:hover  { background:#fef2f2; border-color:#fecaca; }
  .action-btn.deactivate { color:#ef4444; border-color:#fecaca; background:#fef2f2; }
  .action-btn.deactivate:hover { background:#ef4444; color:white; }
  .action-btn.activate   { color:#10c98e; border-color:#a7f3d0; background:#ecfdf5; }
  .action-btn.activate:hover   { background:#10c98e; color:white; }
  .action-btn.history { color:#8b5cf6; } .action-btn.history:hover { background:#f3f0ff; border-color:#8b5cf6; }
  .status-dot { width:7px; height:7px; border-radius:50%; display:inline-block; margin-right:5px; }
  .sm-empty { text-align:center; padding:3rem 2rem; color:#9094a8; font-size:.82rem; }
  .sm-loading { display:flex; align-items:center; justify-content:center; height:200px; gap:10px; color:#9094a8; }
  .sm-spin { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#3b6ef8; border-radius:50%; animation:sp .7s linear infinite; }
  @keyframes sp { to{transform:rotate(360deg)} }
  .mb { position:fixed; inset:0; background:rgba(10,12,28,.65); backdrop-filter:blur(5px); display:flex; align-items:center; justify-content:center; z-index:9999; padding:20px; }
  .mo { background:white; border-radius:20px; width:100%; max-width:520px; max-height:90vh; display:flex; flex-direction:column; box-shadow:0 25px 60px rgba(0,0,0,.25); animation:moIn .25s ease; overflow:hidden; }
  @keyframes moIn { from{opacity:0;transform:scale(.94)} to{opacity:1;transform:scale(1)} }
  .mo-hd { display:flex; align-items:center; justify-content:space-between; padding:18px 22px; border-bottom:1px solid #f0f2f8; flex-shrink:0; }
  .mo-title { font-size:1rem; font-weight:700; color:#0f1123; }
  .mo-x { width:28px; height:28px; border-radius:7px; border:none; background:#f0f2f7; cursor:pointer; display:flex; align-items:center; justify-content:center; }
  .mo-x svg { width:14px; height:14px; stroke-width:2; }
  .mo-bd { padding:20px 22px; overflow-y:auto; }
  .mo-ft { display:flex; gap:8px; justify-content:flex-end; padding:14px 22px; border-top:1px solid #f0f2f8; flex-shrink:0; }
  .form-grid { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
  .form-grid > *, .fg > * { min-width:0; }
  .ff { display:flex; flex-direction:column; gap:5px; }
  .ff.full { grid-column:1/-1; }
  .fl { font-size:.68rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .fi,.fs { font-family:'Sora',sans-serif; font-size:.82rem; padding:9px 11px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; color:#1a1d2e; outline:none; width:100%;min-width:0; }
  .fi:focus,.fs:focus { border-color:#3b6ef8; background:white; }
  .btn-save  { padding:9px 20px; border:none; border-radius:10px; background:#3b6ef8; color:white; font-family:'Sora',sans-serif; font-size:.82rem; font-weight:600; cursor:pointer; }
  .btn-save:disabled { opacity:.6; cursor:not-allowed; }
  .btn-ghost { padding:9px 18px; border:none; border-radius:10px; background:#f0f2f7; color:#6b7194; font-family:'Sora',sans-serif; font-size:.82rem; font-weight:600; cursor:pointer; }
  .sm-cards { display:none; padding:10px; }
  .sm-card  { background:#fafbff; border-radius:12px; border:1px solid #e8ebf4; padding:13px; margin-bottom:8px; }
  .sm-card-top { display:flex; align-items:flex-start; justify-content:space-between; gap:8px; margin-bottom:8px; }
  .sm-card-row { display:flex; justify-content:space-between; align-items:flex-start; font-size:.76rem; padding:4px 0; border-bottom:1px solid #f0f2f8; gap:8px; }
  .sm-card-row:last-of-type { border-bottom:none; }
  .sm-card-label { color:#9094a8; font-weight:600; white-space:nowrap; flex-shrink:0; }
  .sm-card-actions { display:flex; gap:6px; flex-wrap:wrap; margin-top:8px; justify-content:flex-end; }
  @media(max-width:768px) { .sm-title{font-size:1.1rem;} .sm-toolbar{flex-direction:column;align-items:stretch;} .btn-add{margin-left:0;} .form-grid{grid-template-columns:1fr;} .ff.full{grid-column:1;} .sm-tbl-wrap{display:none!important;} .sm-cards{display:block;} }
`;

const initials = n => (n||'').split(' ').slice(0,2).map(w=>w[0]).join('').toUpperCase()||'?';
const fmtMoney = n => n ? `Rs. ${Number(n).toLocaleString('en-PK')}` : '—';

export default function StaffManagement() {
  const navigate = useNavigate();
  const [staff,      setStaff]      = useState([]);
  const hasLoaded = useRef(false);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState('');
  const [desFilter,  setDesFilter]  = useState('');
  const [modal,      setModal]      = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [form,       setForm]       = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [toggling,   setToggling]   = useState({});
  const [exitModal,  setExitModal]  = useState(null); // {staff, exitType}
  const [exitForm,   setExitForm]   = useState({ exitDate:'', note:'' });
  const [reactivateConfirm, setReactivateConfirm] = useState(null); // staff
  const [reactivateSaving,  setReactivateSaving]  = useState(false);

  const load = async () => {
    if (!hasLoaded.current) setLoading(true);
    try {
      const params = new URLSearchParams();
      if (desFilter) params.append('designation', desFilter);
      const { data } = await api.get(`/admin/salary/non-portal-staff?${params}`);
      hasLoaded.current = true;
      hasLoaded.current = true;
      setStaff(Array.isArray(data) ? data : []);
    } catch { toast.error('Failed to load staff'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [desFilter]);

  // Auto-fetch unified employee ID
  const openAdd = async () => {
    setForm(EMPTY_FORM);
    setEditTarget(null);
    setModal('add');
    try {
      const { data } = await api.get('/admin/salary/staff/next-employee-id');
      setForm(prev => ({ ...prev, employeeId: data.employeeId || prev.employeeId }));
    } catch {}
  };

  const openEdit = (s) => {
    setEditTarget(s);
    setForm({
      name:            s.name || '',
      department:      s.department  || 'General',
      designation:     s.designation || 'Peon',
      employeeId:      s.employeeId || '',
      email:           s.email || '',
      phone:           s.phone || '',
      cnic:            s.cnic || '',
      address:         s.address || '',
      joiningDate:     s.joiningDate ? parseDate(s.joiningDate) : '',
      monthlySalary:   s.monthlySalary || '',
      absentDeduction: s.absentDeduction || '',
      lateDeduction:   s.lateDeduction || '',
      notes:           s.notes || '',
    });
    setModal('edit');
  };

  const openExit = (s, exitType) => {
    setExitModal({ staff: s, exitType });
    setExitForm({ exitDate: new Date().toISOString().split('T')[0], note: '' });
  };

  const handleExit = async () => {
    if (!exitForm.note.trim()) { toast.error('Reason is required'); return; }
    try {
      await api.put(`/admin/salary/non-portal-staff/${exitModal.staff._id}/exit`, {
        exitType: exitModal.exitType,
        exitDate: exitForm.exitDate,
        note:     exitForm.note,
      });
      toast.success(`${exitModal.staff.name} marked as ${exitModal.exitType}`);
      setExitModal(null);
      load();
    } catch { toast.error('Failed'); }
  };

  const handleReactivate = (s) => setReactivateConfirm(s);

  const confirmReactivate = async () => {
    if (!reactivateConfirm) return;
    setReactivateSaving(true);
    try {
      await api.put(`/admin/salary/non-portal-staff/${reactivateConfirm._id}/reactivate`);
      toast.success(`${reactivateConfirm.name} reactivated`);
      setReactivateConfirm(null);
      load();
    } catch { toast.error('Failed'); }
    finally { setReactivateSaving(false); }
  };

  const handleSubmit = async () => {
    if (!form.name?.trim()) return toast.error('Name is required');
    setSubmitting(true);
    try {
      if (modal === 'add') {
        await api.post('/admin/salary/non-portal-staff', form);
        toast.success('Staff added');
      } else {
        await api.put(`/admin/salary/non-portal-staff/${editTarget._id}`, form);
        toast.success('Staff updated');
      }
      setModal(null);
      load();
    } catch { toast.error('Failed to save'); }
    finally { setSubmitting(false); }
  };

  const handleToggle = async (s) => {
    setToggling(p => ({ ...p, [s._id]: true }));
    try {
      await api.put(`/admin/salary/non-portal-staff/${s._id}/toggle`);
      toast.success(`${s.name} ${s.isActive !== false ? 'deactivated' : 'activated'}`);
      load();
    } catch { toast.error('Failed'); }
    finally { setToggling(p => ({ ...p, [s._id]: false })); }
  };

  const filtered = staff.filter(s =>
    !search || s.name?.toLowerCase().includes(search.toLowerCase()) ||
    s.employeeId?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="sm"><style>{css}</style>
      <div className="sm-title">Staff Management</div>
      <div className="sm-sub">Manage non-portal staff — medical, sports, security, general and more.</div>

      <div className="sm-toolbar">
        <div className="sm-search-wrap">
          <MagnifyingGlassIcon />
          <input className="sm-search" placeholder="Search by name or ID…"
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="sm-select" value={desFilter} onChange={e => setDesFilter(e.target.value)}>
          <option value="">All Designations</option>
          {sortAsc(ALL_DESIGNATIONS).map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        <button className="btn-add" onClick={openAdd}><PlusIcon />Add Staff</button>
      </div>

      {loading ? (
        <div className="sm-loading"><div className="sm-spin"/>Loading…</div>
      ) : (
        <div className="sm-panel">
          {filtered.length === 0 ? (
            <div className="sm-empty">No staff found. Add non-portal staff using the button above.</div>
          ) : (
            <div className="sm-tbl-wrap">
              <table className="sm-tbl">
                <thead><tr>
                  <th style={{minWidth:180}}>Name</th>
                  <th style={{minWidth:110,textAlign:'center'}}>Department</th>
                  <th style={{minWidth:140,textAlign:'center'}}>Designation</th>
                  <th style={{minWidth:100,textAlign:'center'}}>Employee ID</th>
                  <th style={{minWidth:120,textAlign:'center'}}>Phone</th>
                  <th style={{minWidth:120,textAlign:'center'}}>Monthly Salary</th>
                  <th style={{minWidth:80,textAlign:'center'}}>Status</th>
                  <th style={{minWidth:140,textAlign:'center'}}>Actions</th>
                </tr></thead>
                <tbody>
                  {filtered.map(s => {
                    const color = DEPT_COLORS[s.department] || DEPT_COLORS['Other'];
                    const active = s.isActive !== false;
                    return (
                      <tr key={s._id} style={{opacity: s.exitType ? 0.6 : 1}}>
                        <td>
                          <div style={{display:'flex',alignItems:'center',gap:9}}>
                            <div className="sm-avatar" style={{background:`linear-gradient(135deg,${color},${color}99)`}}>
                              {initials(s.name)}
                            </div>
                            <div>
                              <div style={{fontWeight:600,color:'#0f1123'}}>{s.name}</div>
                              {s.email&&<div style={{fontSize:'.68rem',color:'#9094a8'}}>{s.email}</div>}
                            </div>
                          </div>
                        </td>
                        <td style={{textAlign:'center'}}><span className="des-pill" style={{background:`${color}18`,color}}>{s.department||'General'}</span></td>
                        <td style={{textAlign:'center',fontSize:'.78rem',color:'#3a3f58'}}>{s.designation||'—'}</td>
                        <td style={{textAlign:'center',fontFamily:"'JetBrains Mono',monospace",fontSize:'.76rem',color:'#6b7194'}}>{s.employeeId||'—'}</td>
                        <td style={{textAlign:'center',fontSize:'.78rem',color:'#6b7194'}}>{s.phone||'—'}</td>
                        <td style={{textAlign:'center',fontFamily:"'JetBrains Mono',monospace",fontSize:'.78rem',color:'#7c3aed',fontWeight:600}}>{fmtMoney(s.monthlySalary)}</td>
                        <td style={{textAlign:'center'}}>
                          {s.exitType
                            ? <span style={{fontSize:'.72rem',fontWeight:700,padding:'2px 8px',borderRadius:99,background:s.exitType==='terminated'?'#1a1d2e':'#fef2f2',color:s.exitType==='terminated'?'white':'#ef4444'}}>{s.exitType==='terminated'?'Terminated':'Resigned'}</span>
                            : <span style={{fontSize:'.74rem',fontWeight:600,color:active?'#10c98e':'#9094a8'}}>
                                <span className="status-dot" style={{background:active?'#10c98e':'#9094a8'}}/>
                                {active ? 'Active' : 'Inactive'}
                              </span>
                          }
                        </td>
                        <td>
                          <div style={{display:'flex',gap:5,justifyContent:'center'}}>
                            {!s.exitType && <>
                              <button className={`action-btn ${active?'deactivate':'activate'}`}
                                disabled={toggling[s._id]} onClick={() => handleToggle(s)}
                                title={active?'Deactivate':'Activate'}>
                                {toggling[s._id] ? '…' : active ? <NoSymbolIcon/> : <CheckCircleIcon/>}
                              </button>
                              <button className="action-btn" style={{color:'#d97706'}} onClick={() => openExit(s,'resigned')} title="Mark Resigned">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/></svg>
                              </button>
                              <button className="action-btn" style={{color:'#ef4444'}} onClick={() => openExit(s,'terminated')} title="Mark Terminated">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>
                              </button>
                            </>}
                            {s.exitType &&
                              <button className="action-btn activate" onClick={() => handleReactivate(s)} title="Reactivate"><CheckCircleIcon/></button>
                            }
                            <button className="action-btn edit" onClick={() => openEdit(s)} title="Edit"><PencilIcon/></button>
                            <button className="action-btn history" onClick={() => navigate(`/staff-history/${s._id}?isPortal=false`)} title="Full History"><ClockIcon/></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {filtered.length > 0 && (
            <div className="sm-cards">
              {filtered.map(s => {
                const color = DEPT_COLORS[s.department] || DEPT_COLORS['Other'];
                const active = s.isActive !== false;
                return (
                  <div key={s._id} className="sm-card" style={{opacity: s.exitType ? 0.6 : 1}}>
                    <div className="sm-card-top">
                      <div style={{display:'flex',alignItems:'center',gap:9}}>
                        <div className="sm-avatar" style={{background:`linear-gradient(135deg,${color},${color}99)`}}>{initials(s.name)}</div>
                        <div>
                          <div style={{fontWeight:600,color:'#0f1123'}}>{s.name}</div>
                          {s.email && <div style={{fontSize:'.68rem',color:'#9094a8'}}>{s.email}</div>}
                        </div>
                      </div>
                      {s.exitType
                        ? <span style={{fontSize:'.72rem',fontWeight:700,padding:'2px 8px',borderRadius:99,background:s.exitType==='terminated'?'#1a1d2e':'#fef2f2',color:s.exitType==='terminated'?'white':'#ef4444'}}>{s.exitType==='terminated'?'Terminated':'Resigned'}</span>
                        : <span style={{fontSize:'.74rem',fontWeight:600,color:active?'#10c98e':'#9094a8'}}><span className="status-dot" style={{background:active?'#10c98e':'#9094a8'}}/>{active ? 'Active' : 'Inactive'}</span>
                      }
                    </div>
                    <div className="sm-card-row"><span className="sm-card-label">Department</span><span className="des-pill" style={{background:`${color}18`,color}}>{s.department||'General'}</span></div>
                    <div className="sm-card-row"><span className="sm-card-label">Designation</span><span>{s.designation||'—'}</span></div>
                    <div className="sm-card-row"><span className="sm-card-label">Employee ID</span><span style={{fontFamily:"'JetBrains Mono',monospace"}}>{s.employeeId||'—'}</span></div>
                    <div className="sm-card-row"><span className="sm-card-label">Phone</span><span>{s.phone||'—'}</span></div>
                    <div className="sm-card-row"><span className="sm-card-label">Monthly Salary</span><span style={{fontFamily:"'JetBrains Mono',monospace",color:'#7c3aed',fontWeight:600}}>{fmtMoney(s.monthlySalary)}</span></div>
                    <div className="sm-card-actions">
                      {!s.exitType && <>
                        <button className={`action-btn ${active?'deactivate':'activate'}`} disabled={toggling[s._id]} onClick={() => handleToggle(s)} title={active?'Deactivate':'Activate'}>
                          {toggling[s._id] ? '…' : active ? <NoSymbolIcon/> : <CheckCircleIcon/>}
                        </button>
                        <button className="action-btn" style={{color:'#d97706'}} onClick={() => openExit(s,'resigned')} title="Mark Resigned">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/></svg>
                        </button>
                        <button className="action-btn" style={{color:'#ef4444'}} onClick={() => openExit(s,'terminated')} title="Mark Terminated">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>
                        </button>
                      </>}
                      {s.exitType &&
                        <button className="action-btn activate" onClick={() => handleReactivate(s)} title="Reactivate"><CheckCircleIcon/></button>
                      }
                      <button className="action-btn edit" onClick={() => openEdit(s)} title="Edit"><PencilIcon/></button>
                      <button className="action-btn history" onClick={() => navigate(`/staff-history/${s._id}?isPortal=false`)} title="Full History"><ClockIcon/></button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {modal && createPortal(
        <div className="mb" onClick={() => setModal(null)}>
          <div className="mo" onClick={e => e.stopPropagation()}>
            <div className="mo-hd">
              <div className="mo-title">{modal === 'add' ? 'Add Staff Member' : `Edit — ${editTarget?.name}`}</div>
              <button className="mo-x" onClick={() => setModal(null)}><XMarkIcon /></button>
            </div>
            <div className="mo-bd">
              <div className="form-grid">
                <div className="ff full">
                  <label className="fl">Full Name *</label>
                  <input className="fi" value={form.name} onChange={e=>setForm(p=>({...p,name:e.target.value}))} placeholder="Full name" />
                </div>
                <div className="ff">
                  <label className="fl">Email <span style={{fontSize:'.62rem',color:'#b0b5cc',fontWeight:400,textTransform:'none'}}>optional</span></label>
                  <input type="email" className="fi" value={form.email} onChange={e=>setForm(p=>({...p,email:e.target.value}))} placeholder="staff@example.com" />
                </div>
                <div className="ff">
                  <label className="fl">Department *</label>
                  <select className="fs" value={form.department} onChange={e=>setForm(p=>({...p,department:e.target.value}))}>
                    {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
                <div className="ff">
                  <label className="fl">Designation *</label>
                  <select className="fs" value={form.designation} onChange={e=>setForm(p=>({...p,designation:e.target.value}))}>
                    {ALL_DESIGNATIONS.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
                <div className="ff">
                  <label className="fl">Employee ID <span style={{fontSize:'.62rem',background:'#ecfdf5',color:'#10c98e',padding:'1px 6px',borderRadius:99,fontWeight:600,marginLeft:4}}>auto</span></label>
                  <input className="fi" value={form.employeeId} readOnly style={{background:'#f0f2f7',color:'#9094a8',cursor:'not-allowed'}} />
                </div>
                <div className="ff">
                  <label className="fl">Phone</label>
                  <input className="fi" value={form.phone} onChange={e=>setForm(p=>({...p,phone:formatPhone(e.target.value)}))} placeholder="03xx-xxxxxxx" />
                </div>
                <div className="ff">
                  <label className="fl">CNIC</label>
                  <input className="fi" value={form.cnic} onChange={e=>setForm(p=>({...p,cnic:formatCnic(e.target.value)}))} placeholder="xxxxx-xxxxxxx-x" />
                </div>
                <div className="ff">
                  <label className="fl">Joining Date</label>
                  <input type="date" className="fi" value={form.joiningDate} onChange={e=>setForm(p=>({...p,joiningDate:e.target.value}))} />
                </div>
                <div className="ff">
                  <label className="fl">Monthly Salary (Rs.)</label>
                  <input type="number" min="0" className="fi" value={form.monthlySalary} onChange={e=>setForm(p=>({...p,monthlySalary:e.target.value}))} placeholder="0" />
                </div>
                <div className="ff">
                  <label className="fl">Absent Deduction/day (Rs.)</label>
                  <input type="number" min="0" className="fi" value={form.absentDeduction} onChange={e=>setForm(p=>({...p,absentDeduction:e.target.value}))} placeholder="0" />
                </div>
                <div className="ff">
                  <label className="fl">Late Deduction/min (Rs.)</label>
                  <input type="number" min="0" className="fi" value={form.lateDeduction} onChange={e=>setForm(p=>({...p,lateDeduction:e.target.value}))} placeholder="0" />
                </div>
                <div className="ff full">
                  <label className="fl">Address</label>
                  <input className="fi" value={form.address} onChange={e=>setForm(p=>({...p,address:e.target.value}))} placeholder="Address" />
                </div>
                <div className="ff full">
                  <label className="fl">Notes</label>
                  <input className="fi" value={form.notes} onChange={e=>setForm(p=>({...p,notes:e.target.value}))} placeholder="Optional notes" />
                </div>
              </div>
            </div>
            <div className="mo-ft">
              <button className="btn-ghost" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn-save" onClick={handleSubmit} disabled={submitting}>
                {submitting ? 'Saving…' : modal === 'add' ? 'Add Staff' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      
      , document.body)}

      {/* ── Exit Modal ── */}
      {exitModal && createPortal(
        <div className="mb" onClick={() => setExitModal(null)}>
          <div className="mo" style={{maxWidth:420}} onClick={e => e.stopPropagation()}>
            <div className="mo-hd">
              <div className="mo-title" style={{color:exitModal.exitType==='terminated'?'#ef4444':'#d97706'}}>
                {exitModal.exitType==='terminated' ? '⛔ Mark as Terminated' : '🚪 Mark as Resigned'} — {exitModal.staff.name}
              </div>
              <button className="mo-x" onClick={() => setExitModal(null)}><XMarkIcon /></button>
            </div>
            <div className="mo-bd">
              <div style={{background:exitModal.exitType==='terminated'?'#fef2f2':'#fffbeb',border:`1px solid ${exitModal.exitType==='terminated'?'#fecaca':'#fde68a'}`,borderRadius:9,padding:'10px 14px',fontSize:'.78rem',color:exitModal.exitType==='terminated'?'#991b1b':'#92400e',marginBottom:14}}>
                ⚠️ Once marked, this staff member will be excluded from attendance, salary and leave calculations. You can reactivate them later.
              </div>
              <div className="form-grid">
                <div className="ff">
                  <label className="fl">{exitModal.exitType==='terminated'?'Termination Date':'Resignation Date'}</label>
                  <input type="date" className="fi" value={exitForm.exitDate} onChange={e=>setExitForm(p=>({...p,exitDate:e.target.value}))} />
                </div>
                <div className="ff full">
                  <label className="fl">Reason</label>
                  <input className="fi" required value={exitForm.note} onChange={e=>setExitForm(p=>({...p,note:e.target.value}))} placeholder="Reason or note" />
                </div>
              </div>
            </div>
            <div className="mo-ft">
              <button className="btn-ghost" onClick={() => setExitModal(null)}>Cancel</button>
              <button className="btn-save" style={{background:exitModal.exitType==='terminated'?'#ef4444':'#d97706'}} onClick={handleExit}>
                {exitModal.exitType==='terminated' ? 'Confirm Termination' : 'Confirm Resignation'}
              </button>
            </div>
          </div>
        </div>

      , document.body)}

      <ConfirmModal
        open={!!reactivateConfirm}
        title="Reactivate Staff Member"
        subtitle={reactivateConfirm?.name}
        message={`Reactivate ${reactivateConfirm?.name}? They will be active again.`}
        confirmLabel="Reactivate"
        danger={false}
        onConfirm={confirmReactivate}
        onCancel={() => setReactivateConfirm(null)}
        loading={reactivateSaving}
      />
    </div>
  );
}